import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut, X, Upload, ImageIcon, Package, ShoppingBag, CheckCircle2, DollarSign } from "lucide-react";
import {
  adminMe, fetchProducts, adminUpdateProduct, adminCreateProduct,
  adminDeleteProduct, adminUploadImage, fetchSiteImages, fetchOrders, fulfillOrder, formatApiError,
} from "../lib/api";

const EMPTY = {
  name: "", type: "tea", collection: "nighttime-sleep", ingredients: "", benefits: "",
  best_time: "", vibe: [], price: 8, price_note: "", image: "", extra_note: "", contents: null,
};

const COLLECTION_OPTS = [
  "nighttime-sleep", "morning-energy", "daytime-calm", "immune-seasonal",
  "digestive-comfort", "womens-wellness", "salves", "other", "bundles",
];
const TYPE_OPTS = ["tea", "salve", "other", "bundle"];

function Editor({ initial, isNew, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...EMPTY, ...initial,
    vibe: (initial?.vibe || []).join(", "),
    contents: (initial?.contents || []).join(", "),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) {
      toast.error("Save the product first, then upload its image.");
      return;
    }
    setUploading(true);
    try {
      const res = await adminUploadImage(initial.slug, file);
      setForm((f) => ({ ...f, image: res.url }));
      toast.success("Image uploaded");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name, type: form.type, collection: form.collection,
      ingredients: form.ingredients || null, benefits: form.benefits,
      best_time: form.best_time || null,
      vibe: form.vibe ? form.vibe.split(",").map((s) => s.trim()).filter(Boolean) : [],
      price: parseFloat(form.price) || 0, price_note: form.price_note || null,
      image: form.image, extra_note: form.extra_note || null,
      contents: form.contents ? form.contents.split(",").map((s) => s.trim()).filter(Boolean) : null,
    };
    try {
      if (isNew) await adminCreateProduct(payload);
      else await adminUpdateProduct(initial.slug, payload);
      toast.success(isNew ? "Product created" : "Product updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inp = "w-full bg-cream border border-forest/15 rounded-xl py-2.5 px-3 text-forest text-sm focus:outline-none focus:border-terracotta";
  const lbl = "text-xs uppercase tracking-widest text-forest-soft mb-1 block";

  return (
    <div className="fixed inset-0 z-[80] bg-forest/40 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="admin-editor">
      <div className="bg-cream rounded-3xl w-full max-w-2xl p-8 my-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-3xl text-forest">{isNew ? "New product" : `Edit — ${initial.name}`}</h2>
          <button onClick={onClose} data-testid="editor-close" className="text-forest hover:text-terracotta"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={lbl}>Name</label><input className={inp} value={form.name} onChange={set("name")} data-testid="editor-name" /></div>
          <div><label className={lbl}>Type</label><select className={inp} value={form.type} onChange={set("type")} data-testid="editor-type">{TYPE_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div><label className={lbl}>Collection</label><select className={inp} value={form.collection} onChange={set("collection")} data-testid="editor-collection">{COLLECTION_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div><label className={lbl}>Price ($)</label><input type="number" className={inp} value={form.price} onChange={set("price")} data-testid="editor-price" /></div>
          <div><label className={lbl}>Price note</label><input className={inp} value={form.price_note} onChange={set("price_note")} /></div>
          <div className="sm:col-span-2">
            <label className={lbl}>Product Image</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-24 rounded-xl overflow-hidden bg-cream border border-forest/15 flex items-center justify-center shrink-0">
                {form.image ? (
                  <img src={form.image} alt="preview" className="w-full h-full object-cover" data-testid="editor-image-preview" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-forest-soft" />
                )}
              </div>
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="editor-image-file" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="editor-upload-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-forest/20 text-forest px-4 py-2 text-sm hover:bg-surface disabled:opacity-60"
                >
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload image"}
                </button>
                {isNew && <p className="text-xs text-forest-soft mt-2">Save the product first to enable upload.</p>}
                <input className={`${inp} mt-2`} value={form.image} onChange={set("image")} placeholder="…or paste an image URL" />
              </div>
            </div>
          </div>
          <div className="sm:col-span-2"><label className={lbl}>Ingredients</label><input className={inp} value={form.ingredients} onChange={set("ingredients")} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Benefits</label><textarea rows={2} className={inp} value={form.benefits} onChange={set("benefits")} data-testid="editor-benefits" /></div>
          <div><label className={lbl}>Best time</label><input className={inp} value={form.best_time} onChange={set("best_time")} /></div>
          <div><label className={lbl}>Vibe (comma-separated)</label><input className={inp} value={form.vibe} onChange={set("vibe")} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Bundle contents (comma-separated)</label><input className={inp} value={form.contents} onChange={set("contents")} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Extra note (disclaimer)</label><textarea rows={2} className={inp} value={form.extra_note} onChange={set("extra_note")} /></div>
        </div>
        <div className="flex gap-3 mt-8">
          <button onClick={save} disabled={saving} data-testid="editor-save" className="flex-1 rounded-full bg-forest hover:bg-terracotta text-cream py-3 text-sm uppercase tracking-widest font-semibold disabled:opacity-60" style={{ transition: "background-color 0.3s ease" }}>{saving ? "Saving…" : "Save"}</button>
          <button onClick={onClose} className="rounded-full border border-forest/20 text-forest px-8 py-3 text-sm uppercase tracking-widest">Cancel</button>
        </div>
      </div>
    </div>
  );
}

const SECTIONS = [
  { slug: "site-hero", label: "Home — Hero" },
  { slug: "site-story", label: "Home — Story" },
  { slug: "about-portrait", label: "About — Portrait" },
  { slug: "about-banner", label: "About — Banner" },
];

function SectionImages() {
  const [urls, setUrls] = useState({});
  const [busy, setBusy] = useState(null);
  const refs = useRef({});

  const load = useCallback(() => fetchSiteImages().then(setUrls), []);
  useEffect(() => { load(); }, [load]);

  const upload = (slug) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(slug);
    try {
      const res = await adminUploadImage(slug, file);
      setUrls((u) => ({ ...u, [slug]: res.url }));
      toast.success("Section image updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-12" data-testid="section-images">
      <h2 className="font-serif text-3xl text-forest mb-6">Site Section Images</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {SECTIONS.map((s) => (
          <div key={s.slug} className="bg-cream rounded-2xl p-4">
            <div className="rounded-xl overflow-hidden bg-surface aspect-[4/3] mb-3">
              {urls[s.slug] && <img src={urls[s.slug]} alt={s.label} className="w-full h-full object-cover" data-testid={`section-preview-${s.slug}`} />}
            </div>
            <p className="text-sm text-forest mb-3">{s.label}</p>
            <input ref={(el) => (refs.current[s.slug] = el)} type="file" accept="image/*" className="hidden" onChange={upload(s.slug)} data-testid={`section-file-${s.slug}`} />
            <button
              onClick={() => refs.current[s.slug]?.click()}
              disabled={busy === s.slug}
              data-testid={`section-upload-${s.slug}`}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-forest/20 text-forest py-2 text-sm hover:bg-surface disabled:opacity-60"
            >
              <Upload className="w-4 h-4" /> {busy === s.slug ? "Uploading…" : "Replace"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders() {
  const [data, setData] = useState({ orders: [], stats: {} });
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => fetchOrders().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const markFulfilled = async (sid) => {
    setBusy(sid);
    try {
      await fulfillOrder(sid);
      toast.success("Marked fulfilled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const s = data.stats || {};
  const stats = [
    { label: "Revenue", value: `$${s.revenue ?? 0}`, icon: DollarSign },
    { label: "Paid orders", value: s.paid ?? 0, icon: ShoppingBag },
    { label: "To fulfill", value: s.unfulfilled ?? 0, icon: Package },
  ];

  return (
    <div data-testid="orders-view">
      <div className="grid sm:grid-cols-3 gap-5 mb-8">
        {stats.map((st) => (
          <div key={st.label} className="bg-cream rounded-2xl p-6 flex items-center gap-4">
            <span className="w-11 h-11 rounded-full bg-surface flex items-center justify-center"><st.icon className="w-5 h-5 text-terracotta" /></span>
            <div>
              <p className="text-xs uppercase tracking-widest text-forest-soft">{st.label}</p>
              <p className="font-serif text-3xl text-forest">{st.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-cream rounded-3xl overflow-hidden" data-testid="orders-table">
        <table className="w-full text-left">
          <thead className="bg-forest text-cream text-xs uppercase tracking-widest">
            <tr>
              <th className="py-4 px-6">Date</th>
              <th className="py-4 px-4">Customer</th>
              <th className="py-4 px-4">Items</th>
              <th className="py-4 px-4">Total</th>
              <th className="py-4 px-4">Payment</th>
              <th className="py-4 px-6 text-right">Fulfillment</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-forest-soft">No orders yet.</td></tr>
            )}
            {data.orders.map((o) => {
              const paid = o.payment_status === "paid";
              const fulfilled = o.fulfillment_status === "fulfilled";
              return (
                <tr key={o.session_id} className="border-b border-forest/5 align-top" data-testid={`order-row-${o.session_id}`}>
                  <td className="py-3 px-6 text-sm text-forest-soft whitespace-nowrap">{(o.created_at || "").slice(0, 10)}</td>
                  <td className="py-3 px-4 text-sm text-forest">
                    {o.customer_name && <div>{o.customer_name}</div>}
                    <div className="text-forest-soft">{o.customer_email || "\u2014"}</div>
                  </td>
                  <td className="py-3 px-4 text-sm text-forest max-w-xs">
                    {(o.items || []).map((it, i) => (
                      <div key={i}>{it.qty}× {it.name}</div>
                    ))}
                  </td>
                  <td className="py-3 px-4 font-semibold text-forest whitespace-nowrap">${o.amount}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs uppercase tracking-wide rounded-full px-3 py-1 ${paid ? "bg-sage/30 text-forest" : "bg-surface text-forest-soft"}`}>{o.payment_status}</span>
                  </td>
                  <td className="py-3 px-6 text-right">
                    {paid ? (
                      fulfilled ? (
                        <span className="inline-flex items-center gap-1 text-sm text-forest-soft"><CheckCircle2 className="w-4 h-4 text-sage" /> Fulfilled</span>
                      ) : (
                        <button onClick={() => markFulfilled(o.session_id)} disabled={busy === o.session_id} data-testid={`fulfill-${o.session_id}`} className="rounded-full bg-forest hover:bg-terracotta text-cream px-4 py-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-60" style={{ transition: "background-color 0.3s ease" }}>{busy === o.session_id ? "\u2026" : "Mark fulfilled"}</button>
                      )
                    ) : (
                      <span className="text-sm text-forest-soft">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function Admin() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // {initial, isNew}
  const [view, setView] = useState("products");

  const load = useCallback(() => fetchProducts().then(setProducts), []);

  useEffect(() => {
    if (!localStorage.getItem("ssw_admin_token")) {
      nav("/admin/login");
      return;
    }
    adminMe()
      .then(() => {
        setReady(true);
        load();
      })
      .catch(() => {
        localStorage.removeItem("ssw_admin_token");
        nav("/admin/login");
      });
  }, [nav, load]);

  const remove = async (slug, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await adminDeleteProduct(slug);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const logout = () => {
    localStorage.removeItem("ssw_admin_token");
    nav("/admin/login");
  };

  if (!ready) return <div className="min-h-screen flex items-center justify-center text-forest-soft">Loading studio…</div>;

  return (
    <div className="min-h-screen bg-surface">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-terracotta mb-1">Serendipitous Sips</p>
            <h1 className="font-serif text-5xl text-forest">Product Studio</h1>
          </div>
          <div className="flex gap-3">
            {view === "products" && (
              <button onClick={() => setEditing({ initial: EMPTY, isNew: true })} data-testid="admin-add-product" className="inline-flex items-center gap-2 rounded-full bg-forest hover:bg-terracotta text-cream px-6 py-3 text-sm uppercase tracking-widest font-semibold" style={{ transition: "background-color 0.3s ease" }}><Plus className="w-4 h-4" /> Add</button>
            )}
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-forest/20 text-forest px-6 py-3 text-sm uppercase tracking-widest"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>

        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {[["products", "Products"], ["orders", "Orders"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setView(k)}
              data-testid={`tab-${k}`}
              className={`rounded-full px-6 py-2.5 text-sm uppercase tracking-widest ${view === k ? "bg-forest text-cream" : "border border-forest/20 text-forest hover:bg-cream"}`}
              style={{ transition: "background-color 0.3s ease, color 0.3s ease" }}
            >
              {label}
            </button>
          ))}
        </div>

        {view === "orders" && <Orders />}

        {view === "products" && (
        <>
        <div className="bg-cream rounded-3xl overflow-hidden" data-testid="admin-product-list">
          <table className="w-full text-left">
            <thead className="bg-forest text-cream text-xs uppercase tracking-widest">
              <tr>
                <th className="py-4 px-6">Product</th>
                <th className="py-4 px-4 hidden md:table-cell">Type</th>
                <th className="py-4 px-4 hidden md:table-cell">Collection</th>
                <th className="py-4 px-4">Price</th>
                <th className="py-4 px-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.slug} className="border-b border-forest/5" data-testid={`admin-row-${p.slug}`}>
                  <td className="py-3 px-6 flex items-center gap-3">
                    <img src={p.image} alt="" className="w-10 h-12 object-cover rounded-md" />
                    <span className="font-serif text-lg text-forest">{p.name}</span>
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell text-forest-soft text-sm">{p.type}</td>
                  <td className="py-3 px-4 hidden md:table-cell text-forest-soft text-sm">{p.collection}</td>
                  <td className="py-3 px-4 text-forest font-semibold">${p.price}</td>
                  <td className="py-3 px-6">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditing({ initial: p, isNew: false })} data-testid={`admin-edit-${p.slug}`} className="w-9 h-9 rounded-full bg-surface flex items-center justify-center text-forest hover:bg-sage hover:text-cream" style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(p.slug, p.name)} data-testid={`admin-delete-${p.slug}`} className="w-9 h-9 rounded-full bg-surface flex items-center justify-center text-forest hover:bg-terracotta hover:text-cream" style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <SectionImages />
        </>
        )}
      </div>

      {editing && (
        <Editor initial={editing.initial} isNew={editing.isNew} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
